import { unsubscribeDevice } from '../lib/push.ts';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  Comment, CreateTaskInput, CreateWorkspaceInput, HomePayload, Invitation, InvitationPreview, InviteInput, Me, Member,
  Notification, OrgDetail, SearchResult, Session, Task, TaskListQuery, TaskState, UpdateTaskInput, Workspace, RoleKey,
  CalendarItem, ConnectorInfo, ContentItem, ContentListQuery, CreateContentInput, EditorialConfig, EventInput,
  GenerateMonthResult, UpdateContentInput, ConnectionInfo, WorkspaceSection, WorkspaceSectionInput,
  SectionBlock, SectionBlockInput, SectionBlockUpdateInput, HidableModule,
  SocialConnectorInfo, SocialConnectorKey, SocialConnectionInfo, ContentLabel, ContentLabelInput,
  SocialContentList, InsightsSnapshot, MarketingInfo, Campaign, SocialPlatform, WorkspaceChannel, SocialAccount, WorkspaceMember,
  YoutubePrivacy, YoutubePublishResult, TiktokCreatorOptions, TiktokPublishQuery, TiktokPublishResult,
} from '@unitly/contracts';
import { api, apiUpload, apiUploadWithProgress, qs } from './client.ts';

// ------------------------------------------------------------------ keys
export const keys = {
  me: ['me'] as const,
  org: (o: string) => ['org', o] as const,
  home: (o: string) => ['home', o] as const,
  tasks: (o: string, f?: TaskListQuery) => (f ? (['tasks', o, f] as const) : (['tasks', o] as const)),
  task: (o: string, id: string) => ['task', o, id] as const,
  comments: (o: string, id: string) => ['comments', o, id] as const,
  states: (o: string) => ['states', o] as const,
  workspaces: (o: string) => ['workspaces', o] as const,
  workspace: (o: string, id: string) => ['workspace', o, id] as const,
  members: (o: string) => ['members', o] as const,
  invitations: (o: string) => ['invitations', o] as const,
  notifications: (o: string) => ['notifications', o] as const,
  sessions: ['sessions'] as const,
  search: (o: string, q: string) => ['search', o, q] as const,
};

const orgPath = (o: string) => `/orgs/${o}`;

// ------------------------------------------------------------------ account
export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => api<Me>('/me'), retry: false, staleTime: 60_000 });

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; password: string }) => api<void>('/auth/login', 'POST', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}
export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; email: string; password: string; organizationName: string }) =>
      api<{ organization: { id: string; slug: string } }>('/auth/signup', 'POST', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => { await unsubscribeDevice(); await api<void>('/auth/logout', 'POST'); },
    onSuccess: () => qc.clear(),
  });
}
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { displayName?: string; timeZone?: string }) => api<void>('/me', 'PATCH', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}
function invalidateAvatarEverywhere(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: keys.me });
  qc.invalidateQueries({ queryKey: ['members'] });
  qc.invalidateQueries({ queryKey: ['workspace'] });
}
export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => apiUpload<{ avatarUrl: string }>('/me/avatar', file),
    onSuccess: () => invalidateAvatarEverywhere(qc),
  });
}
export function useDeleteAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/me/avatar', 'DELETE'),
    onSuccess: () => invalidateAvatarEverywhere(qc),
  });
}
export const useSessions = () => useQuery({ queryKey: keys.sessions, queryFn: () => api<Session[]>('/me/sessions') });
export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/me/sessions/${id}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.sessions }),
  });
}
export const useChangePassword = () =>
  useMutation({ mutationFn: (v: { currentPassword: string; newPassword: string }) => api<void>('/me/password', 'POST', v) });

export function useVerifyEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api<void>('/auth/verify-email', 'POST', { code }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}
export const useResendVerification = () => useMutation({ mutationFn: () => api<void>('/auth/resend-verification', 'POST') });
export const useForgotPassword = () =>
  useMutation({ mutationFn: (email: string) => api<void>('/auth/forgot-password', 'POST', { email }) });
export const useResetPassword = () =>
  useMutation({ mutationFn: (v: { token: string; password: string }) => api<void>('/auth/reset-password', 'POST', v) });

export const useInvitationPreview = (token: string) =>
  useQuery({ queryKey: ['invitation', token], queryFn: () => api<InvitationPreview>(`/invitations/${token}`), retry: false });
export function useAcceptInvitation(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name?: string; password?: string }) =>
      api<{ organization: { id: string; slug: string } }>(`/invitations/${token}/accept`, 'POST', v),
    // Void return (not `return qc.invalidateQueries(...)`): the mutation machinery awaits whatever this
    // returns before running the caller's own onSuccess (the `navigate()` in InvitePage). Awaiting the
    // refetch here let the invitation-preview query re-fetch first, 404 (now consumed) and briefly flash
    // "invito non valido" before the redirect ever fired.
    onSuccess: () => { void qc.invalidateQueries({ queryKey: keys.me }); },
  });
}

// ------------------------------------------------------------------ organization
export const useOrg = (o: string) => useQuery({ queryKey: keys.org(o), queryFn: () => api<OrgDetail>(orgPath(o)), staleTime: 60_000 });
export function useUpdateOrg(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string }) => api<void>(orgPath(o), 'PATCH', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.org(o) });
      qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}
export const useMembers = (o: string, enabled = true) =>
  useQuery({ queryKey: keys.members(o), queryFn: () => api<Member[]>(`${orgPath(o)}/members`), enabled, staleTime: 60_000 });
export function useUpdateMember(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: string; role: RoleKey }) => api<void>(`${orgPath(o)}/members/${v.userId}`, 'PATCH', { role: v.role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.members(o) }),
  });
}
export function useRemoveMember(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api<void>(`${orgPath(o)}/members/${userId}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.members(o) }),
  });
}
export const useMemberWorkspaces = (o: string, userId: string | null) =>
  useQuery({
    queryKey: ['member-workspaces', o, userId ?? ''],
    queryFn: () => api<{ workspaceIds: string[] }>(`${orgPath(o)}/members/${userId}/workspaces`),
    enabled: Boolean(userId),
  });
export const useInvitations = (o: string, enabled: boolean) =>
  useQuery({ queryKey: keys.invitations(o), queryFn: () => api<Invitation[]>(`${orgPath(o)}/invitations`), enabled });
export function useInvite(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: InviteInput) => api<Invitation>(`${orgPath(o)}/invitations`, 'POST', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invitations(o) }),
  });
}
export function useRevokeInvitation(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/invitations/${id}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invitations(o) }),
  });
}

// ------------------------------------------------------------------ workspaces
export const useWorkspaces = (o: string) =>
  useQuery({ queryKey: keys.workspaces(o), queryFn: () => api<Workspace[]>(`${orgPath(o)}/workspaces`) });
export const useWorkspace = (o: string, id: string | undefined) =>
  useQuery({
    queryKey: keys.workspace(o, id ?? ''),
    queryFn: () => api<Workspace & { members: WorkspaceMember[] }>(`${orgPath(o)}/workspaces/${id}`),
    enabled: Boolean(id),
  });
export function useCreateWorkspace(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CreateWorkspaceInput) => api<Workspace>(`${orgPath(o)}/workspaces`, 'POST', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
      qc.invalidateQueries({ queryKey: keys.home(o) });
    },
  });
}
export function useUploadWorkspaceLogo(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, file }: { workspaceId: string; file: File }) => apiUpload<{ logoUrl: string }>(`${orgPath(o)}/workspaces/${workspaceId}/logo`, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
      qc.invalidateQueries({ queryKey: keys.home(o) });
    },
  });
}
export function useDeleteWorkspaceLogo(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) => api<void>(`${orgPath(o)}/workspaces/${workspaceId}/logo`, 'DELETE'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
      qc.invalidateQueries({ queryKey: keys.home(o) });
    },
  });
}
export function useDeleteWorkspace(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) => api<void>(`${orgPath(o)}/workspaces/${workspaceId}`, 'DELETE'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
      qc.invalidateQueries({ queryKey: keys.home(o) });
    },
  });
}
export function useSetModuleVisibility(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { workspaceId: string; module: HidableModule; hidden: boolean }) =>
      api<void>(`${orgPath(o)}/workspaces/${v.workspaceId}/module-visibility`, 'PATCH', { module: v.module, hidden: v.hidden }),
    onSuccess: (_v, { workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
      qc.invalidateQueries({ queryKey: keys.workspace(o, workspaceId) });
    },
  });
}
export function useTransferWorkspace(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { workspaceId: string; userId: string }) => api<void>(`${orgPath(o)}/workspaces/${v.workspaceId}/transfer`, 'POST', { userId: v.userId }),
    onSuccess: (_v, { workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
      qc.invalidateQueries({ queryKey: keys.workspace(o, workspaceId) });
    },
  });
}
export const useSections = (o: string, ws: string) =>
  useQuery({ queryKey: ['sections', o, ws], queryFn: () => api<WorkspaceSection[]>(`${orgPath(o)}/workspaces/${ws}/sections`), enabled: Boolean(ws) });
export function useCreateSection(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: WorkspaceSectionInput) => api<WorkspaceSection>(`${orgPath(o)}/workspaces/${ws}/sections`, 'POST', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sections', o, ws] }),
  });
}
export function useUpdateSection(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: Partial<WorkspaceSectionInput> }) => api<WorkspaceSection>(`${orgPath(o)}/workspaces/${ws}/sections/${v.id}`, 'PATCH', v.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sections', o, ws] }),
  });
}
export function useReorderSections(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) => api<void>(`${orgPath(o)}/workspaces/${ws}/sections/reorder`, 'POST', { orderedIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sections', o, ws] }),
  });
}
export function useDeleteSection(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/workspaces/${ws}/sections/${id}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sections', o, ws] }),
  });
}
const blockKey = (o: string, ws: string, sectionId: string) => ['section-blocks', o, ws, sectionId] as const;
export const useSectionBlocks = (o: string, ws: string, sectionId: string) =>
  useQuery({ queryKey: blockKey(o, ws, sectionId), queryFn: () => api<SectionBlock[]>(`${orgPath(o)}/workspaces/${ws}/sections/${sectionId}/blocks`), enabled: Boolean(sectionId) });
export function useCreateBlock(o: string, ws: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: SectionBlockInput) => api<SectionBlock>(`${orgPath(o)}/workspaces/${ws}/sections/${sectionId}/blocks`, 'POST', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: blockKey(o, ws, sectionId) }),
  });
}
export function useUpdateBlock(o: string, ws: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: SectionBlockUpdateInput }) => api<SectionBlock>(`${orgPath(o)}/workspaces/${ws}/sections/${sectionId}/blocks/${v.id}`, 'PATCH', v.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: blockKey(o, ws, sectionId) }),
  });
}
export function useReorderBlocks(o: string, ws: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) => api<void>(`${orgPath(o)}/workspaces/${ws}/sections/${sectionId}/blocks/reorder`, 'POST', { orderedIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: blockKey(o, ws, sectionId) }),
  });
}
export function useDeleteBlock(o: string, ws: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/workspaces/${ws}/sections/${sectionId}/blocks/${id}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: blockKey(o, ws, sectionId) }),
  });
}
export function useWorkspaceMember(o: string, workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: string; remove?: boolean }) =>
      v.remove
        ? api<void>(`${orgPath(o)}/workspaces/${workspaceId}/members/${v.userId}`, 'DELETE')
        : api<void>(`${orgPath(o)}/workspaces/${workspaceId}/members`, 'POST', { userId: v.userId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.workspace(o, workspaceId) }),
  });
}
/** Custom display title + personal tab visibility for one workspace member (see WorkspaceMemberSettingsInput). */
export function useUpdateWorkspaceMemberSettings(o: string, workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: string; position: string | null; hiddenModules: HidableModule[] }) =>
      api<void>(`${orgPath(o)}/workspaces/${workspaceId}/members/${v.userId}`, 'PATCH', { position: v.position, hiddenModules: v.hiddenModules }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.workspace(o, workspaceId) });
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
    },
  });
}

// ------------------------------------------------------------------ tasks
export const useStates = (o: string) =>
  useQuery({ queryKey: keys.states(o), queryFn: () => api<TaskState[]>(`${orgPath(o)}/states`), staleTime: 5 * 60_000 });
export const useHome = (o: string) =>
  useQuery({ queryKey: keys.home(o), queryFn: () => api<HomePayload>(`${orgPath(o)}/home`), refetchInterval: 60_000 });
export const useTasks = (o: string, f: TaskListQuery) =>
  useQuery({ queryKey: keys.tasks(o, f), queryFn: () => api<Task[]>(`${orgPath(o)}/tasks${qs(f as Record<string, string>)}`) });
export const useTask = (o: string, id: string | null) =>
  useQuery({ queryKey: keys.task(o, id ?? ''), queryFn: () => api<Task>(`${orgPath(o)}/tasks/${id}`), enabled: Boolean(id) });
export const useComments = (o: string, id: string | null) =>
  useQuery({ queryKey: keys.comments(o, id ?? ''), queryFn: () => api<Comment[]>(`${orgPath(o)}/tasks/${id}/comments`), enabled: Boolean(id) });

/** After any task change: refresh every list that may contain it. */
function refreshTasks(qc: QueryClient, o: string, task?: Task) {
  qc.invalidateQueries({ queryKey: keys.tasks(o) });
  qc.invalidateQueries({ queryKey: keys.home(o) });
  qc.invalidateQueries({ queryKey: keys.workspaces(o) });
  if (task) qc.setQueryData(keys.task(o, task.id), task);
}

/** Optimistically patch a task everywhere it appears in the cache. */
function patchCachedTask(qc: QueryClient, o: string, id: string, patch: (t: Task) => Task) {
  qc.setQueriesData<Task[]>({ queryKey: keys.tasks(o) }, (list) => list?.map((t) => (t.id === id ? patch(t) : t)));
  qc.setQueryData<Task>(keys.task(o, id), (t) => (t ? patch(t) : t));
  qc.setQueryData<HomePayload>(keys.home(o), (h) =>
    h && { ...h, today: h.today.map((t) => (t.id === id ? patch(t) : t)), overdue: h.overdue.map((t) => (t.id === id ? patch(t) : t)), upcoming: h.upcoming.map((t) => (t.id === id ? patch(t) : t)) },
  );
}

export function useCreateTask(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CreateTaskInput) => api<Task>(`${orgPath(o)}/tasks`, 'POST', v),
    onSuccess: (task) => refreshTasks(qc, o, task),
  });
}

export function useUpdateTask(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: UpdateTaskInput }) => api<Task>(`${orgPath(o)}/tasks/${v.id}`, 'PATCH', v.patch),
    onMutate: ({ id, patch }) => {
      patchCachedTask(qc, o, id, (t) => ({
        ...t,
        ...(patch.title !== undefined && { title: patch.title }),
        ...(patch.description !== undefined && { description: patch.description }),
        ...(patch.priority !== undefined && { priority: patch.priority }),
      }));
    },
    onSuccess: (task) => refreshTasks(qc, o, task),
    onError: () => refreshTasks(qc, o),
  });
}

export function useToggleTask(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; done: boolean }) => api<Task>(`${orgPath(o)}/tasks/${v.id}/${v.done ? 'complete' : 'reopen'}`, 'POST'),
    onMutate: async ({ id, done }) => {
      await qc.cancelQueries({ queryKey: keys.tasks(o) });
      patchCachedTask(qc, o, id, (t) => ({
        ...t,
        completedAt: done ? new Date().toISOString() : null,
        state: done ? { ...t.state, category: 'done', name: 'Fatto' } : { ...t.state, category: 'todo', name: 'Da fare' },
      }));
    },
    onSettled: (task) => refreshTasks(qc, o, task ?? undefined),
  });
}

export function useDeleteTask(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/tasks/${id}`, 'DELETE'),
    onSuccess: () => refreshTasks(qc, o),
  });
}

export function useAddComment(o: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api<Comment>(`${orgPath(o)}/tasks/${taskId}/comments`, 'POST', { body }),
    onSuccess: (c) => {
      qc.setQueryData<Comment[]>(keys.comments(o, taskId), (list) => [...(list ?? []), c]);
      qc.invalidateQueries({ queryKey: keys.task(o, taskId) });
    },
  });
}
export function useEditComment(o: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: string }) => api<Comment>(`${orgPath(o)}/tasks/${taskId}/comments/${v.id}`, 'PATCH', { body: v.body }),
    onSuccess: (c) => qc.setQueryData<Comment[]>(keys.comments(o, taskId), (list) => list?.map((x) => (x.id === c.id ? c : x))),
  });
}
export function useDeleteComment(o: string, taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/tasks/${taskId}/comments/${id}`, 'DELETE'),
    onSuccess: (_v, id) => qc.setQueryData<Comment[]>(keys.comments(o, taskId), (list) => list?.filter((x) => x.id !== id)),
  });
}

// ------------------------------------------------------------------ notifications & search
export const useNotifications = (o: string) =>
  useQuery({
    queryKey: keys.notifications(o),
    queryFn: () => api<{ items: Notification[]; unread: number }>(`${orgPath(o)}/notifications`),
    refetchInterval: 30_000,
  });
export function useMarkRead(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { ids?: string[]; all?: boolean }) => api<void>(`${orgPath(o)}/notifications/read`, 'POST', v),
    onMutate: ({ ids, all }) => {
      qc.setQueryData<{ items: Notification[]; unread: number }>(keys.notifications(o), (d) => {
        if (!d) return d;
        const now = new Date().toISOString();
        const items = d.items.map((n) => (all || ids?.includes(n.id) ? { ...n, readAt: n.readAt ?? now } : n));
        return { items, unread: items.filter((n) => !n.readAt).length };
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: keys.notifications(o) });
      qc.invalidateQueries({ queryKey: keys.home(o) });
    },
  });
}
export const useSearch = (o: string, q: string) =>
  useQuery({
    queryKey: keys.search(o, q),
    queryFn: () => api<SearchResult[]>(`${orgPath(o)}/search${qs({ q })}`),
    enabled: q.trim().length >= 2,
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });

// ------------------------------------------------------------------ editorial (PED)

export const edKeys = {
  config: (o: string, ws: string) => ['editorial-config', o, ws] as const,
  items: (o: string) => ['editorial-items', o] as const,
  itemList: (o: string, q: ContentListQuery) => ['editorial-items', o, q] as const,
  item: (o: string, id: string) => ['editorial-item', o, id] as const,
  comments: (o: string, id: string) => ['editorial-comments', o, id] as const,
  calendar: (o: string) => ['calendar', o] as const,
  calendarRange: (o: string, from: string, to: string) => ['calendar', o, from, to] as const,
  connectors: (o: string) => ['connectors', o] as const,
};

export const useEditorialConfig = (o: string, ws: string | undefined) =>
  useQuery({
    queryKey: edKeys.config(o, ws ?? ''),
    queryFn: () => api<EditorialConfig>(`${orgPath(o)}/workspaces/${ws}/editorial`),
    enabled: Boolean(ws),
    staleTime: 60_000,
  });

/** Any PED configuration change: every settings endpoint returns the full, updated config. */
export function useEditorialSettings(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { path: string; method: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) =>
      api<EditorialConfig>(`${orgPath(o)}/workspaces/${ws}/editorial${v.path}`, v.method, v.body),
    onSuccess: (cfg) => {
      qc.setQueryData(edKeys.config(o, ws), cfg);
      qc.invalidateQueries({ queryKey: edKeys.items(o) });
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
    },
  });
}

export const useContentItems = (o: string, q: ContentListQuery, enabled = true) =>
  useQuery({
    queryKey: edKeys.itemList(o, q),
    queryFn: () => api<ContentItem[]>(`${orgPath(o)}/editorial/items${qs(q as Record<string, string>)}`),
    enabled,
    placeholderData: (prev) => prev,
  });

export const useContentItem = (o: string, id: string | null) =>
  useQuery({ queryKey: edKeys.item(o, id ?? ''), queryFn: () => api<ContentItem>(`${orgPath(o)}/editorial/items/${id}`), enabled: Boolean(id) });

function refreshContent(qc: QueryClient, o: string, item?: ContentItem) {
  qc.invalidateQueries({ queryKey: edKeys.items(o) });
  qc.invalidateQueries({ queryKey: edKeys.calendar(o) });
  qc.invalidateQueries({ queryKey: ['editorial-config', o] });
  if (item) qc.setQueryData(edKeys.item(o, item.id), item);
}

/** Uploads the video of a content item to the YouTube channel of its workspace (see youtube-publish.ts). */
export function usePublishYoutube(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; channelId: string; file: File; privacy: YoutubePrivacy; onProgress: (f: number) => void }) =>
      apiUploadWithProgress<YoutubePublishResult>(
        `${orgPath(o)}/editorial/items/${v.id}/channels/${v.channelId}/youtube${qs({ privacy: v.privacy })}`, v.file, v.onProgress),
    onSuccess: (r) => refreshContent(qc, o, r.item),
  });
}

/** The creator's current TikTok options (visibility choices, interactions): asked fresh each time the panel opens. */
export function useTiktokCreatorOptions(o: string, itemId: string, channelId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['tiktok-creator', o, itemId, channelId],
    queryFn: () => api<TiktokCreatorOptions>(`${orgPath(o)}/editorial/items/${itemId}/channels/${channelId}/tiktok`),
    enabled, staleTime: 0, retry: false,
  });
}

export function usePublishTiktok(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; channelId: string; file: File; options: TiktokPublishQuery; onProgress: (f: number) => void }) =>
      apiUploadWithProgress<TiktokPublishResult>(
        `${orgPath(o)}/editorial/items/${v.id}/channels/${v.channelId}/tiktok${qs(v.options as Record<string, string>)}`, v.file, v.onProgress),
    onSuccess: (r) => refreshContent(qc, o, r.item),
  });
}

export function useCreateContent(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CreateContentInput) => api<ContentItem>(`${orgPath(o)}/editorial/items`, 'POST', v),
    onSuccess: (item) => refreshContent(qc, o, item),
  });
}

export function useUpdateContent(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: UpdateContentInput }) => api<ContentItem>(`${orgPath(o)}/editorial/items/${v.id}`, 'PATCH', v.patch),
    onSuccess: (item) => refreshContent(qc, o, item),
    onError: () => refreshContent(qc, o),
  });
}

/** submit · approve · request-changes · channel publication · delete */
export function useContentAction(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; action: 'submit' | 'approve' | 'request-changes' | 'delete' | `channels/${string}`; body?: unknown }): Promise<ContentItem | void> =>
      v.action === 'delete'
        ? api<void>(`${orgPath(o)}/editorial/items/${v.id}`, 'DELETE')
        : api<ContentItem>(`${orgPath(o)}/editorial/items/${v.id}/${v.action}`, v.action.startsWith('channels/') ? 'PATCH' : 'POST', v.body),
    onSuccess: (item) => {
      refreshContent(qc, o, item ?? undefined);
      qc.invalidateQueries({ queryKey: keys.notifications(o) });
    },
  });
}

export function useGenerateMonth(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (month: string) => api<GenerateMonthResult>(`${orgPath(o)}/workspaces/${ws}/editorial/generate`, 'POST', { month }),
    onSuccess: () => refreshContent(qc, o),
  });
}

export const useContentComments = (o: string, id: string) =>
  useQuery({ queryKey: edKeys.comments(o, id), queryFn: () => api<Comment[]>(`${orgPath(o)}/editorial/items/${id}/comments`) });

export function useAddContentComment(o: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api<Comment>(`${orgPath(o)}/editorial/items/${id}/comments`, 'POST', { body }),
    onSuccess: (c) => {
      qc.setQueryData<Comment[]>(edKeys.comments(o, id), (l) => [...(l ?? []), c]);
      qc.invalidateQueries({ queryKey: edKeys.item(o, id) });
    },
  });
}

// ------------------------------------------------------------------ calendar & connectors
export const useCalendar = (o: string, from: string, to: string) =>
  useQuery({
    queryKey: edKeys.calendarRange(o, from, to),
    queryFn: () => api<CalendarItem[]>(`${orgPath(o)}/calendar${qs({ from, to })}`),
    placeholderData: (prev) => prev,
    refetchInterval: 5 * 60_000,
  });

export function useEventMutation(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; input?: EventInput; remove?: boolean }): Promise<{ id: string } | void> =>
      v.remove ? api<void>(`${orgPath(o)}/calendar/events/${v.id}`, 'DELETE')
        : v.id ? api<void>(`${orgPath(o)}/calendar/events/${v.id}`, 'PATCH', v.input)
          : api<{ id: string }>(`${orgPath(o)}/calendar/events`, 'POST', v.input),
    onSuccess: () => qc.invalidateQueries({ queryKey: edKeys.calendar(o) }),
  });
}

export const useConnectors = (o: string) =>
  useQuery({ queryKey: edKeys.connectors(o), queryFn: () => api<ConnectorInfo[]>(`${orgPath(o)}/connectors`) });

export function useConnectorAction(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { action: 'authorize'; scope: 'user' | 'organization' | 'workspace' | 'workspace_user'; workspaceId?: string } | { action: 'sync' | 'disconnect'; id: string } | { action: 'select'; id: string; calendarIds: string[] }) => {
      if (v.action === 'authorize') return api<{ url: string }>(`${orgPath(o)}/connectors/google_calendar/authorize`, 'POST', { scope: v.scope, workspaceId: v.workspaceId ?? null });
      if (v.action === 'sync') return api<ConnectionInfo>(`${orgPath(o)}/connections/${v.id}/sync`, 'POST');
      if (v.action === 'select') return api<ConnectionInfo>(`${orgPath(o)}/connections/${v.id}`, 'PATCH', { selectedCalendarIds: v.calendarIds });
      return api<void>(`${orgPath(o)}/connections/${v.id}`, 'DELETE');
    },
    onSuccess: (res) => {
      if (res && 'url' in res) return; // navigation to Google follows
      qc.invalidateQueries({ queryKey: edKeys.connectors(o) });
      qc.invalidateQueries({ queryKey: edKeys.calendar(o) });
    },
  });
}

// ------------------------------------------------------------------ social (Meta/Threads: connect, content, insights, marketing)
const socialKeys = {
  connectors: (o: string) => ['social-connectors', o] as const,
  content: (o: string, ws: string, platform?: SocialPlatform, labelId?: string) => ['social-content', o, ws, platform, labelId] as const,
  labels: (o: string, ws: string) => ['content-labels', o, ws] as const,
  insights: (o: string, ws: string) => ['social-insights', o, ws] as const,
  marketing: (o: string, ws: string) => ['social-marketing', o, ws] as const,
  campaigns: (o: string, ws: string, adAccountId: string) => ['social-campaigns', o, ws, adAccountId] as const,
  channels: (o: string, ws: string) => ['workspace-channels', o, ws] as const,
};

export const useSocialConnectors = (o: string) =>
  useQuery({ queryKey: socialKeys.connectors(o), queryFn: () => api<SocialConnectorInfo[]>(`${orgPath(o)}/connectors/social`) });

export function useSocialConnectorAction(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      v: { action: 'authorize'; key: SocialConnectorKey; scope: 'workspace' | 'organization'; workspaceId?: string }
        | { action: 'select'; id: string; selectedPageIds: string[] }
        | { action: 'disconnect'; id: string },
    ) => {
      if (v.action === 'authorize') return api<{ url: string }>(`${orgPath(o)}/connectors/social/${v.key}/authorize`, 'POST', { scope: v.scope, workspaceId: v.workspaceId });
      if (v.action === 'select') return api<SocialConnectionInfo>(`${orgPath(o)}/social-connections/${v.id}`, 'PATCH', { selectedPageIds: v.selectedPageIds });
      return api<void>(`${orgPath(o)}/social-connections/${v.id}`, 'DELETE');
    },
    onSuccess: (res) => {
      if (res && 'url' in res) return; // navigation to Meta/Threads follows
      qc.invalidateQueries({ queryKey: socialKeys.connectors(o) });
    },
  });
}

export const useSocialContent = (o: string, workspaceId: string, platform?: SocialPlatform, labelId?: string) =>
  useQuery({
    queryKey: socialKeys.content(o, workspaceId, platform, labelId),
    queryFn: () => api<SocialContentList>(`${orgPath(o)}/social-content${qs({ workspaceId, platform, labelId })}`),
    enabled: Boolean(workspaceId),
  });

/** Read-only: which company account (if any) this workspace's tabs are currently using, per platform.
 * Assigning happens from Settings → Integrazioni (useSocialAccounts/useAssignSocialAccount below). */
export const useWorkspaceChannels = (o: string, workspaceId: string) =>
  useQuery({
    queryKey: socialKeys.channels(o, workspaceId),
    queryFn: () => api<WorkspaceChannel[]>(`${orgPath(o)}/workspace-channels${qs({ workspaceId })}`),
    enabled: Boolean(workspaceId),
  });

export const useSocialAccounts = (o: string) =>
  useQuery({ queryKey: ['social-accounts', o], queryFn: () => api<SocialAccount[]>(`${orgPath(o)}/social-accounts`) });

export function useAssignSocialAccount(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { connectionId: string; platform: SocialAccount['platform']; pageId: string | null; workspaceId: string | null }) =>
      api<SocialAccount[]>(`${orgPath(o)}/social-accounts/assignment`, 'PUT', v),
    onSuccess: (accounts) => {
      qc.setQueryData(['social-accounts', o], accounts);
      qc.invalidateQueries({ queryKey: ['workspace-channels', o] });
      qc.invalidateQueries({ queryKey: ['social-content', o] });
      qc.invalidateQueries({ queryKey: ['social-insights', o] });
      qc.invalidateQueries({ queryKey: ['social-marketing', o] });
    },
  });
}

export const useContentLabels = (o: string, workspaceId: string) =>
  useQuery({
    queryKey: socialKeys.labels(o, workspaceId),
    queryFn: () => api<ContentLabel[]>(`${orgPath(o)}/content-labels${qs({ workspaceId })}`),
    enabled: Boolean(workspaceId),
  });

export function useSaveContentLabel(o: string, workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: ContentLabelInput & { id?: string; workspaceId: string }) =>
      v.id ? api<ContentLabel>(`${orgPath(o)}/content-labels/${v.id}`, 'PATCH', { name: v.name, color: v.color })
        : api<ContentLabel>(`${orgPath(o)}/content-labels`, 'POST', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: socialKeys.labels(o, workspaceId) }),
  });
}

export function useDeleteContentLabel(o: string, workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/content-labels/${id}`, 'DELETE'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialKeys.labels(o, workspaceId) });
      qc.invalidateQueries({ queryKey: ['social-content', o, workspaceId] });
    },
  });
}

export function useAssignContentLabels(o: string, workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { connectionId: string; externalId: string; labelIds: string[] }) =>
      api<ContentLabel[]>(`${orgPath(o)}/social-content/${v.connectionId}/${v.externalId}/labels`, 'PATCH', { workspaceId, labelIds: v.labelIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social-content', o, workspaceId] }),
  });
}

export const useSocialInsights = (o: string, workspaceId: string) =>
  useQuery({
    queryKey: socialKeys.insights(o, workspaceId),
    queryFn: () => api<InsightsSnapshot[]>(`${orgPath(o)}/social-insights${qs({ workspaceId })}`),
    enabled: Boolean(workspaceId),
  });

export const useSocialMarketing = (o: string, workspaceId: string) =>
  useQuery({
    queryKey: socialKeys.marketing(o, workspaceId),
    queryFn: () => api<MarketingInfo | null>(`${orgPath(o)}/social-marketing${qs({ workspaceId })}`),
    enabled: Boolean(workspaceId),
  });

export const useCampaigns = (o: string, workspaceId: string, adAccountId: string) =>
  useQuery({
    queryKey: socialKeys.campaigns(o, workspaceId, adAccountId),
    queryFn: () => api<Campaign[]>(`${orgPath(o)}/social-marketing/campaigns${qs({ workspaceId, adAccountId })}`),
    enabled: Boolean(workspaceId && adAccountId),
  });

export function useSetCampaignStatus(o: string, workspaceId: string, adAccountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { campaignId: string; status: 'ACTIVE' | 'PAUSED' }) =>
      api<Campaign>(`${orgPath(o)}/social-marketing/campaigns/${v.campaignId}`, 'PATCH', { workspaceId, adAccountId, status: v.status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: socialKeys.campaigns(o, workspaceId, adAccountId) }),
  });
}

// ------------------------------------------------------------------ preferences (customizable Home)
import {
  normalizeEmailNotificationPrefs, normalizeHomeLayout, normalizeWorkspaceShelf,
  type EmailNotificationPrefs, type HomeLayout, type WorkspaceShelf,
} from '@unitly/contracts';

export const useHomeLayout = (o: string) =>
  useQuery({
    queryKey: ['pref', o, 'home_layout'],
    queryFn: async () => normalizeHomeLayout((await api<{ value: unknown }>(`${orgPath(o)}/me/preferences/home_layout`)).value),
    staleTime: 60_000,
  });

export function useSetHomeLayout(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (layout: HomeLayout) => api<void>(`${orgPath(o)}/me/preferences/home_layout`, 'PATCH', { value: layout }),
    onSuccess: (_data, layout) => qc.setQueryData(['pref', o, 'home_layout'], layout),
  });
}

/** Pin/reorder for the workspace list, normalized against the workspaces the member can currently see. */
export function useWorkspaceShelf(o: string) {
  const workspaces = useWorkspaces(o);
  const ids = (workspaces.data ?? []).map((w) => w.id);
  const idsKey = ids.join(',');
  return useQuery({
    queryKey: ['pref', o, 'workspace_shelf', idsKey],
    queryFn: async () => normalizeWorkspaceShelf((await api<{ value: unknown }>(`${orgPath(o)}/me/preferences/workspace_shelf`)).value, ids),
    enabled: workspaces.data !== undefined,
    staleTime: 60_000,
  });
}
export function useSetWorkspaceShelf(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shelf: WorkspaceShelf) => api<void>(`${orgPath(o)}/me/preferences/workspace_shelf`, 'PATCH', { value: shelf }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pref', o, 'workspace_shelf'] }),
  });
}

export const useEmailNotificationPrefs = (o: string) =>
  useQuery({
    queryKey: ['pref', o, 'email_notifications'],
    queryFn: async () => normalizeEmailNotificationPrefs((await api<{ value: unknown }>(`${orgPath(o)}/me/preferences/email_notifications`)).value),
    staleTime: 60_000,
  });

export function useSetEmailNotificationPrefs(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: EmailNotificationPrefs) => api<void>(`${orgPath(o)}/me/preferences/email_notifications`, 'PATCH', { value: prefs }),
    onSuccess: (_data, prefs) => qc.setQueryData(['pref', o, 'email_notifications'], prefs),
  });
}

// ------------------------------------------------------------------ pages (documents)
import type {
  CompanyInput, ContactInput, CreateDealInput, CreatePageInput, CrmActivity, CrmCompanyDetail, CrmCompanySummary,
  CrmConfig, CrmContactDetail, CrmContactSummary, CrmSentEmail, Deal, DealStageInput, GmailStatus, GmailThreadMessage,
  PageDetail, PageSummary, PageVersionSummary, SendCrmEmailInput, UpdateDealInput, UpdatePageInput,
} from '@unitly/contracts';

export const pageKeys = {
  list: (o: string, ws: string, parentId?: string | null) => ['pages', o, ws, parentId ?? 'root'] as const,
  templates: (o: string, ws: string) => ['page-templates', o, ws] as const,
  detail: (o: string, id: string) => ['page', o, id] as const,
  versions: (o: string, id: string) => ['page-versions', o, id] as const,
};

export const usePages = (o: string, workspaceId: string, parentPageId?: string | null) =>
  useQuery({
    queryKey: pageKeys.list(o, workspaceId, parentPageId),
    queryFn: () => api<PageSummary[]>(`${orgPath(o)}/pages${qs({ workspaceId, ...(parentPageId ? { parentPageId } : {}) })}`),
    enabled: Boolean(workspaceId),
  });

export const usePageTemplates = (o: string, workspaceId: string) =>
  useQuery({
    queryKey: pageKeys.templates(o, workspaceId),
    queryFn: () => api<PageSummary[]>(`${orgPath(o)}/pages/templates${qs({ workspaceId })}`),
    enabled: Boolean(workspaceId),
  });

export const usePage = (o: string, id: string | null) =>
  useQuery({ queryKey: pageKeys.detail(o, id ?? ''), queryFn: () => api<PageDetail>(`${orgPath(o)}/pages/${id}`), enabled: Boolean(id) });

export const usePageVersions = (o: string, id: string | null) =>
  useQuery({ queryKey: pageKeys.versions(o, id ?? ''), queryFn: () => api<PageVersionSummary[]>(`${orgPath(o)}/pages/${id}/versions`), enabled: Boolean(id) });

function refreshPages(qc: QueryClient, o: string, workspaceId: string, parentPageId: string | null) {
  qc.invalidateQueries({ queryKey: pageKeys.list(o, workspaceId, parentPageId) });
  qc.invalidateQueries({ queryKey: pageKeys.templates(o, workspaceId) });
}

export function useCreatePage(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CreatePageInput) => api<PageDetail>(`${orgPath(o)}/pages`, 'POST', v),
    onSuccess: (page) => refreshPages(qc, o, page.workspaceId, page.parentPageId),
  });
}

export function useUpdatePage(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: UpdatePageInput }) => api<PageDetail>(`${orgPath(o)}/pages/${v.id}`, 'PATCH', v.patch),
    onSuccess: (page) => {
      qc.setQueryData(pageKeys.detail(o, page.id), page);
      refreshPages(qc, o, page.workspaceId, page.parentPageId);
    },
  });
}

export function useDeletePage(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; workspaceId: string; parentPageId: string | null }) => api<void>(`${orgPath(o)}/pages/${v.id}`, 'DELETE'),
    onSuccess: (_r, v) => refreshPages(qc, o, v.workspaceId, v.parentPageId),
  });
}

export function useRestorePageVersion(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; versionId: string }) => api<PageDetail>(`${orgPath(o)}/pages/${v.id}/restore`, 'POST', { versionId: v.versionId }),
    onSuccess: (page) => {
      qc.setQueryData(pageKeys.detail(o, page.id), page);
      qc.invalidateQueries({ queryKey: pageKeys.versions(o, page.id) });
    },
  });
}

// ------------------------------------------------------------------ CRM & Sales
export const crmKeys = {
  config: (o: string, ws: string) => ['crm-config', o, ws] as const,
  companies: (o: string, ws: string) => ['crm-companies', o, ws] as const,
  company: (o: string, id: string) => ['crm-company', o, id] as const,
  contacts: (o: string, ws: string) => ['crm-contacts', o, ws] as const,
  contact: (o: string, id: string) => ['crm-contact', o, id] as const,
  deals: (o: string, ws: string) => ['crm-deals', o, ws] as const,
  activities: (o: string, ws: string, filter: string) => ['crm-activities', o, ws, filter] as const,
};

export const useCrmConfig = (o: string, ws: string | undefined) =>
  useQuery({
    queryKey: crmKeys.config(o, ws ?? ''),
    queryFn: () => api<CrmConfig>(`${orgPath(o)}/workspaces/${ws}/crm`),
    enabled: Boolean(ws),
    staleTime: 60_000,
  });

export function useCrmSettings(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { path: string; method: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) =>
      api<CrmConfig>(`${orgPath(o)}/workspaces/${ws}/crm${v.path}`, v.method, v.body),
    onSuccess: (cfg) => {
      qc.setQueryData(crmKeys.config(o, ws), cfg);
      qc.invalidateQueries({ queryKey: crmKeys.deals(o, ws) });
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
    },
  });
}

export const useCrmCompanies = (o: string, ws: string) =>
  useQuery({ queryKey: crmKeys.companies(o, ws), queryFn: () => api<CrmCompanySummary[]>(`${orgPath(o)}/workspaces/${ws}/crm/companies`), enabled: Boolean(ws) });

export const useCrmCompany = (o: string, ws: string, id: string | null) =>
  useQuery({ queryKey: crmKeys.company(o, id ?? ''), queryFn: () => api<CrmCompanyDetail>(`${orgPath(o)}/workspaces/${ws}/crm/companies/${id}`), enabled: Boolean(ws && id) });

export function useCrmCompanyMutation(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; patch: CompanyInput }): Promise<CrmCompanyDetail> =>
      v.id ? api<CrmCompanyDetail>(`${orgPath(o)}/workspaces/${ws}/crm/companies/${v.id}`, 'PATCH', v.patch) : api<CrmCompanyDetail>(`${orgPath(o)}/workspaces/${ws}/crm/companies`, 'POST', v.patch),
    onSuccess: (c) => {
      qc.setQueryData(crmKeys.company(o, c.id), c);
      qc.invalidateQueries({ queryKey: crmKeys.companies(o, ws) });
      qc.invalidateQueries({ queryKey: crmKeys.deals(o, ws) });
    },
  });
}

export function useDeleteCrmCompany(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/workspaces/${ws}/crm/companies/${id}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: crmKeys.companies(o, ws) }),
  });
}

export const useCrmContacts = (o: string, ws: string) =>
  useQuery({ queryKey: crmKeys.contacts(o, ws), queryFn: () => api<CrmContactSummary[]>(`${orgPath(o)}/workspaces/${ws}/crm/contacts`), enabled: Boolean(ws) });

export const useCrmContact = (o: string, ws: string, id: string | null) =>
  useQuery({ queryKey: crmKeys.contact(o, id ?? ''), queryFn: () => api<CrmContactDetail>(`${orgPath(o)}/workspaces/${ws}/crm/contacts/${id}`), enabled: Boolean(ws && id) });

export function useCrmContactMutation(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; patch: ContactInput }): Promise<CrmContactDetail> =>
      v.id ? api<CrmContactDetail>(`${orgPath(o)}/workspaces/${ws}/crm/contacts/${v.id}`, 'PATCH', v.patch) : api<CrmContactDetail>(`${orgPath(o)}/workspaces/${ws}/crm/contacts`, 'POST', v.patch),
    onSuccess: (c) => {
      qc.setQueryData(crmKeys.contact(o, c.id), c);
      qc.invalidateQueries({ queryKey: crmKeys.contacts(o, ws) });
    },
  });
}

export function useDeleteCrmContact(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/workspaces/${ws}/crm/contacts/${id}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: crmKeys.contacts(o, ws) }),
  });
}

export const useDeals = (o: string, ws: string) =>
  useQuery({ queryKey: crmKeys.deals(o, ws), queryFn: () => api<Deal[]>(`${orgPath(o)}/workspaces/${ws}/crm/deals`), enabled: Boolean(ws) });

function refreshDeals(qc: QueryClient, o: string, ws: string) {
  qc.invalidateQueries({ queryKey: crmKeys.deals(o, ws) });
  qc.invalidateQueries({ queryKey: crmKeys.config(o, ws) });
  qc.invalidateQueries({ queryKey: crmKeys.companies(o, ws) });
}

export function useCreateDeal(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CreateDealInput) => api<Deal>(`${orgPath(o)}/workspaces/${ws}/crm/deals`, 'POST', v),
    onSuccess: () => refreshDeals(qc, o, ws),
  });
}

export function useUpdateDeal(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: UpdateDealInput }) => api<Deal>(`${orgPath(o)}/workspaces/${ws}/crm/deals/${v.id}`, 'PATCH', v.patch),
    onSuccess: () => refreshDeals(qc, o, ws),
  });
}

export function useMoveDeal(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; stateId: string }) => api<Deal>(`${orgPath(o)}/workspaces/${ws}/crm/deals/${v.id}/move`, 'POST', { stateId: v.stateId }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: crmKeys.deals(o, ws) });
      const prev = qc.getQueryData<Deal[]>(crmKeys.deals(o, ws));
      qc.setQueryData<Deal[]>(crmKeys.deals(o, ws), (list) => list?.map((d) => (d.id === v.id ? { ...d, state: { ...d.state, id: v.stateId } } : d)));
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(crmKeys.deals(o, ws), ctx.prev),
    onSettled: () => refreshDeals(qc, o, ws),
  });
}

export function useDeleteDeal(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/workspaces/${ws}/crm/deals/${id}`, 'DELETE'),
    onSuccess: () => refreshDeals(qc, o, ws),
  });
}

export const useCrmActivities = (o: string, ws: string, filter: { dealId?: string; companyId?: string; contactId?: string }) =>
  useQuery({
    queryKey: crmKeys.activities(o, ws, JSON.stringify(filter)),
    queryFn: () => api<CrmActivity[]>(`${orgPath(o)}/workspaces/${ws}/crm/activities${qs(filter as Record<string, string>)}`),
    enabled: Boolean(ws && (filter.dealId || filter.companyId || filter.contactId)),
  });

export function useAddCrmActivity(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { kind: string; body: string; dealId?: string; companyId?: string; contactId?: string }) =>
      api<CrmActivity>(`${orgPath(o)}/workspaces/${ws}/crm/activities`, 'POST', v),
    onSuccess: (_a, v) => qc.invalidateQueries({ queryKey: crmKeys.activities(o, ws, JSON.stringify({ dealId: v.dealId, companyId: v.companyId, contactId: v.contactId })) }),
  });
}

export function useCrmStages(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { path: string; method: 'POST' | 'PATCH' | 'DELETE'; body?: DealStageInput }) =>
      api<CrmConfig>(`${orgPath(o)}/workspaces/${ws}/crm${v.path}`, v.method, v.body),
    onSuccess: (cfg) => qc.setQueryData(crmKeys.config(o, ws), cfg),
  });
}

// ------------------------------------------------------------------ CRM email (send via Gmail + open tracking)
export const useGmailStatus = (o: string, ws: string) =>
  useQuery({ queryKey: ['gmail-status', o, ws], queryFn: () => api<GmailStatus>(`${orgPath(o)}/workspaces/${ws}/crm/gmail`), enabled: Boolean(ws) });

export const useCrmEmails = (o: string, ws: string, filter: { contactId?: string; dealId?: string }) =>
  useQuery({
    queryKey: ['crm-emails', o, ws, filter.contactId, filter.dealId],
    queryFn: () => api<CrmSentEmail[]>(`${orgPath(o)}/workspaces/${ws}/crm/emails${qs(filter as Record<string, string>)}`),
    enabled: Boolean(ws && (filter.contactId || filter.dealId)),
  });

export function useSendCrmEmail(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: SendCrmEmailInput) => api<CrmSentEmail>(`${orgPath(o)}/workspaces/${ws}/crm/emails`, 'POST', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm-emails', o, ws] });
      qc.invalidateQueries({ queryKey: ['crm-activities', o, ws] });
    },
  });
}

/** Live from Gmail (never stored): the real conversation with a contact. Loaded on demand (a button
 * click), not automatically, since it costs a handful of Gmail API calls each time. */
export const useGmailThread = (o: string, ws: string, contactId: string, load: boolean) =>
  useQuery({
    queryKey: ['gmail-thread', o, ws, contactId],
    queryFn: () => api<GmailThreadMessage[]>(`${orgPath(o)}/workspaces/${ws}/crm/contacts/${contactId}/thread`),
    enabled: Boolean(ws && contactId && load),
    staleTime: 60_000,
  });

// ------------------------------------------------------------------ customizable widgets (charts), shared by finance & crm
import type { ChartSeries, Widget, WidgetInput, WidgetModule } from '@unitly/contracts';

export const useWidgets = (o: string, ws: string, module: WidgetModule, sectionId?: string) =>
  useQuery({
    queryKey: ['widgets', o, ws, module, sectionId],
    queryFn: () => api<Widget[]>(`${orgPath(o)}/workspaces/${ws}/widgets${qs({ module, sectionId })}`),
    enabled: Boolean(ws),
  });

export function useWidgetMutation(o: string, ws: string, module: WidgetModule, sectionId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; patch: WidgetInput }): Promise<Widget> =>
      v.id ? api<Widget>(`${orgPath(o)}/workspaces/${ws}/widgets/${v.id}`, 'PATCH', v.patch) : api<Widget>(`${orgPath(o)}/workspaces/${ws}/widgets`, 'POST', { ...v.patch, module, sectionId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets', o, ws, module, sectionId] }),
  });
}

export function useDeleteWidget(o: string, ws: string, module: WidgetModule, sectionId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/workspaces/${ws}/widgets/${id}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets', o, ws, module, sectionId] }),
  });
}

export const useChart = (o: string, ws: string, module: WidgetModule, widget: Pick<Widget, 'metrics' | 'groupBy' | 'dateRange'> | null) =>
  useQuery({
    queryKey: ['chart', o, ws, module, widget?.metrics.join(','), widget?.groupBy, widget?.dateRange],
    queryFn: () => api<ChartSeries>(`${orgPath(o)}/workspaces/${ws}/${module}/chart${qs({ metrics: widget!.metrics.join(','), groupBy: widget!.groupBy, dateRange: widget!.dateRange })}`),
    enabled: Boolean(ws && widget),
    staleTime: 30_000,
  });

// ------------------------------------------------------------------ finance (contabilità)
import type {
  AccountInput, CategoryInput, CreateTransactionInput, FinanceAccount, FinanceConfig, FinanceTransaction, TransactionListQuery, UpdateTransactionInput,
} from '@unitly/contracts';

const finKeys = {
  config: (o: string, ws: string) => ['finance-config', o, ws] as const,
  transactions: (o: string, ws: string, q: string) => ['finance-transactions', o, ws, q] as const,
};

export const useFinanceConfig = (o: string, ws: string | undefined) =>
  useQuery({ queryKey: finKeys.config(o, ws ?? ''), queryFn: () => api<FinanceConfig>(`${orgPath(o)}/workspaces/${ws}/finance`), enabled: Boolean(ws), staleTime: 60_000 });

export function useFinanceSettings(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { path: string; method: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) =>
      api<FinanceConfig>(`${orgPath(o)}/workspaces/${ws}/finance${v.path}`, v.method, v.body),
    onSuccess: (cfg) => {
      qc.setQueryData(finKeys.config(o, ws), cfg);
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
    },
  });
}

export function useAccountMutation(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; patch: AccountInput }) =>
      api<FinanceConfig>(`${orgPath(o)}/workspaces/${ws}/finance/accounts${v.id ? `/${v.id}` : ''}`, v.id ? 'PATCH' : 'POST', v.patch),
    onSuccess: (cfg) => qc.setQueryData(finKeys.config(o, ws), cfg),
  });
}

export function useDeleteAccount(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<FinanceConfig>(`${orgPath(o)}/workspaces/${ws}/finance/accounts/${id}`, 'DELETE'),
    onSuccess: (cfg) => qc.setQueryData(finKeys.config(o, ws), cfg),
  });
}

export function useCategoryMutation(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; patch: CategoryInput }) =>
      api<FinanceConfig>(`${orgPath(o)}/workspaces/${ws}/finance/categories${v.id ? `/${v.id}` : ''}`, v.id ? 'PATCH' : 'POST', v.patch),
    onSuccess: (cfg) => qc.setQueryData(finKeys.config(o, ws), cfg),
  });
}

export function useDeleteCategory(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<FinanceConfig>(`${orgPath(o)}/workspaces/${ws}/finance/categories/${id}`, 'DELETE'),
    onSuccess: (cfg) => qc.setQueryData(finKeys.config(o, ws), cfg),
  });
}

export const useTransactions = (o: string, ws: string, q: TransactionListQuery = {}) =>
  useQuery({
    queryKey: finKeys.transactions(o, ws, JSON.stringify(q)),
    queryFn: () => api<FinanceTransaction[]>(`${orgPath(o)}/workspaces/${ws}/finance/transactions${qs(q as Record<string, string>)}`),
    enabled: Boolean(ws),
  });

function refreshFinance(qc: QueryClient, o: string, ws: string) {
  qc.invalidateQueries({ queryKey: ['finance-transactions', o, ws] });
  qc.invalidateQueries({ queryKey: finKeys.config(o, ws) });
  qc.invalidateQueries({ queryKey: ['chart', o, ws, 'finance'] });
}

export function useCreateTransaction(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CreateTransactionInput) => api<FinanceTransaction>(`${orgPath(o)}/workspaces/${ws}/finance/transactions`, 'POST', v),
    onSuccess: () => refreshFinance(qc, o, ws),
  });
}

export function useUpdateTransaction(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: UpdateTransactionInput }) => api<FinanceTransaction>(`${orgPath(o)}/workspaces/${ws}/finance/transactions/${v.id}`, 'PATCH', v.patch),
    onSuccess: () => refreshFinance(qc, o, ws),
  });
}

export function useDeleteTransaction(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/workspaces/${ws}/finance/transactions/${id}`, 'DELETE'),
    onSuccess: () => refreshFinance(qc, o, ws),
  });
}

// ------------------------------------------------------------------ HR
import type { Employee, EmployeeProfileInput, HrConfig, LeaveRequest, LeaveRequestInput } from '@unitly/contracts';

const hrKeys = {
  config: (o: string, ws: string) => ['hr-config', o, ws] as const,
  employees: (o: string, ws: string) => ['hr-employees', o, ws] as const,
  leaves: (o: string, ws: string, userId?: string) => ['hr-leaves', o, ws, userId ?? 'all'] as const,
};

export const useHrConfig = (o: string, ws: string | undefined) =>
  useQuery({ queryKey: hrKeys.config(o, ws ?? ''), queryFn: () => api<HrConfig>(`${orgPath(o)}/workspaces/${ws}/hr`), enabled: Boolean(ws), staleTime: 60_000 });

export function useHrSettings(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<HrConfig>(`${orgPath(o)}/workspaces/${ws}/hr/enable`, 'POST'),
    onSuccess: (cfg) => {
      qc.setQueryData(hrKeys.config(o, ws), cfg);
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
    },
  });
}

export const useEmployees = (o: string, ws: string) =>
  useQuery({ queryKey: hrKeys.employees(o, ws), queryFn: () => api<Employee[]>(`${orgPath(o)}/workspaces/${ws}/hr/employees`), enabled: Boolean(ws) });

export function useUpdateEmployee(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: string; patch: EmployeeProfileInput }) => api<Employee>(`${orgPath(o)}/workspaces/${ws}/hr/employees/${v.userId}`, 'PATCH', v.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: hrKeys.employees(o, ws) }),
  });
}

export const useLeaveRequests = (o: string, ws: string, userId?: string) =>
  useQuery({ queryKey: hrKeys.leaves(o, ws, userId), queryFn: () => api<LeaveRequest[]>(`${orgPath(o)}/workspaces/${ws}/hr/leave-requests${qs({ userId })}`), enabled: Boolean(ws) });

function refreshLeaves(qc: QueryClient, o: string, ws: string) {
  qc.invalidateQueries({ queryKey: ['hr-leaves', o, ws] });
  qc.invalidateQueries({ queryKey: hrKeys.employees(o, ws) });
}

export function useCreateLeaveRequest(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: LeaveRequestInput) => api<LeaveRequest>(`${orgPath(o)}/workspaces/${ws}/hr/leave-requests`, 'POST', v),
    onSuccess: () => refreshLeaves(qc, o, ws),
  });
}

export function useReviewLeaveRequest(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: 'approved' | 'rejected' }) => api<LeaveRequest>(`${orgPath(o)}/workspaces/${ws}/hr/leave-requests/${v.id}/review`, 'POST', { status: v.status }),
    onSuccess: () => refreshLeaves(qc, o, ws),
  });
}

export function useCancelLeaveRequest(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/workspaces/${ws}/hr/leave-requests/${id}`, 'DELETE'),
    onSuccess: () => refreshLeaves(qc, o, ws),
  });
}

// ------------------------------------------------------------------ chat (org-wide: DMs, groups, workspace channels)
import type { ChatChannel, ChatChannelInput, ChatMessage, GroupInput, MyOrgProfile, NicknameInput, PersonResult } from '@unitly/contracts';

const chatKeys = {
  conversations: (o: string) => ['chat-conversations', o] as const,
  messages: (o: string, id: string) => ['chat-messages', o, id] as const,
};

export const useConversations = (o: string) =>
  useQuery({ queryKey: chatKeys.conversations(o), queryFn: () => api<ChatChannel[]>(`${orgPath(o)}/chat/conversations`), refetchInterval: 15_000 });

export function useStartDm(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api<ChatChannel>(`${orgPath(o)}/chat/dm`, 'POST', { userId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: chatKeys.conversations(o) }),
  });
}

export function useCreateGroup(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: GroupInput) => api<ChatChannel>(`${orgPath(o)}/chat/groups`, 'POST', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: chatKeys.conversations(o) }),
  });
}

export function useCreateChannel(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: ChatChannelInput) => api<ChatChannel>(`${orgPath(o)}/chat/channels`, 'POST', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: chatKeys.conversations(o) }),
  });
}

export function useChannelAction(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; action: 'join' | 'leave' | 'archive'; name?: string; topic?: string | null }): Promise<ChatChannel | void> =>
      v.action === 'archive' ? api<void>(`${orgPath(o)}/chat/channels/${v.id}`, 'DELETE')
        : api<ChatChannel>(`${orgPath(o)}/chat/channels/${v.id}/${v.action}`, 'POST'),
    onSuccess: () => qc.invalidateQueries({ queryKey: chatKeys.conversations(o) }),
  });
}

export function useRenameConversation(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; name?: string; topic?: string | null }) => api<ChatChannel>(`${orgPath(o)}/chat/channels/${v.id}`, 'PATCH', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: chatKeys.conversations(o) }),
  });
}

export function useAddChannelMember(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { channelId: string; userId: string }) => api<void>(`${orgPath(o)}/chat/channels/${v.channelId}/members`, 'POST', { userId: v.userId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: chatKeys.conversations(o) }),
  });
}

export const useChatMessages = (o: string, channelId: string | null) =>
  useQuery({
    queryKey: chatKeys.messages(o, channelId ?? ''),
    queryFn: () => api<ChatMessage[]>(`${orgPath(o)}/chat/channels/${channelId}/messages`),
    enabled: Boolean(channelId),
    refetchInterval: 4_000,
  });

export function useSendMessage(o: string, channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api<ChatMessage>(`${orgPath(o)}/chat/channels/${channelId}/messages`, 'POST', { body }),
    onSuccess: (msg) => {
      qc.setQueryData<ChatMessage[]>(chatKeys.messages(o, channelId), (list) => [...(list ?? []), msg]);
      // Update the sidebar's preview/unread badge for this conversation immediately, rather than
      // waiting on the invalidated refetch below to land - sending a message also marks the channel
      // read server-side, so showing a stale (pre-send) unread badge for even a moment is never correct.
      qc.setQueryData<ChatChannel[]>(chatKeys.conversations(o), (list) =>
        list?.map((c) => (c.id === channelId
          ? { ...c, unreadCount: 0, lastMessage: { body: msg.body, createdAt: msg.createdAt, mine: true }, lastMessageAt: msg.createdAt }
          : c)));
      qc.invalidateQueries({ queryKey: chatKeys.conversations(o) });
    },
  });
}

export function useEditMessage(o: string, channelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: string }) => api<ChatMessage>(`${orgPath(o)}/chat/channels/${channelId}/messages/${v.id}`, 'PATCH', { body: v.body }),
    onSuccess: (msg) => qc.setQueryData<ChatMessage[]>(chatKeys.messages(o, channelId), (list) => list?.map((m) => (m.id === msg.id ? msg : m))),
  });
}

export const usePeopleSearch = (o: string, q: string) =>
  useQuery({
    queryKey: ['people-search', o, q],
    queryFn: () => api<PersonResult[]>(`${orgPath(o)}/people/search${qs({ q })}`),
    enabled: q.trim().length > 0,
  });

export const useMyNickname = (o: string) =>
  useQuery({ queryKey: ['my-nickname', o], queryFn: () => api<MyOrgProfile>(`${orgPath(o)}/me/profile`), staleTime: 60_000 });

export function useSetNickname(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nickname: NicknameInput['nickname']) => api<void>(`${orgPath(o)}/me/nickname`, 'PATCH', { nickname }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-nickname', o] }),
  });
}

// ------------------------------------------------------------------ support tickets
import type { CreateTicketInput, Ticket, TicketConfig, TicketListQuery, UpdateTicketInput } from '@unitly/contracts';

const ticketKeys = {
  config: (o: string, ws: string) => ['ticket-config', o, ws] as const,
  items: (o: string, ws: string, q: string) => ['ticket-items', o, ws, q] as const,
  comments: (o: string, ws: string, id: string) => ['ticket-comments', o, ws, id] as const,
};

export const useTicketConfig = (o: string, ws: string | undefined) =>
  useQuery({ queryKey: ticketKeys.config(o, ws ?? ''), queryFn: () => api<TicketConfig>(`${orgPath(o)}/workspaces/${ws}/tickets`), enabled: Boolean(ws), staleTime: 60_000 });

export function useEnableTickets(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<TicketConfig>(`${orgPath(o)}/workspaces/${ws}/tickets/enable`, 'POST'),
    onSuccess: (cfg) => {
      qc.setQueryData(ticketKeys.config(o, ws), cfg);
      qc.invalidateQueries({ queryKey: keys.workspaces(o) });
    },
  });
}

export const useTickets = (o: string, ws: string, q: TicketListQuery = {}) =>
  useQuery({
    queryKey: ticketKeys.items(o, ws, JSON.stringify(q)),
    queryFn: () => api<Ticket[]>(`${orgPath(o)}/workspaces/${ws}/tickets/items${qs(q as Record<string, string>)}`),
    enabled: Boolean(ws),
  });

export const useTicket = (o: string, ws: string, id: string | null) =>
  useQuery({ queryKey: ['ticket', o, ws, id ?? ''], queryFn: () => api<Ticket>(`${orgPath(o)}/workspaces/${ws}/tickets/items/${id}`), enabled: Boolean(ws && id) });

function refreshTickets(qc: QueryClient, o: string, ws: string) {
  qc.invalidateQueries({ queryKey: ['ticket-items', o, ws] });
  qc.invalidateQueries({ queryKey: ticketKeys.config(o, ws) });
}

export function useCreateTicket(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CreateTicketInput) => api<Ticket>(`${orgPath(o)}/workspaces/${ws}/tickets/items`, 'POST', v),
    onSuccess: () => refreshTickets(qc, o, ws),
  });
}

export function useUpdateTicket(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: UpdateTicketInput }) => api<Ticket>(`${orgPath(o)}/workspaces/${ws}/tickets/items/${v.id}`, 'PATCH', v.patch),
    onSuccess: () => refreshTickets(qc, o, ws),
  });
}

export function useDeleteTicket(o: string, ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/workspaces/${ws}/tickets/items/${id}`, 'DELETE'),
    onSuccess: () => refreshTickets(qc, o, ws),
  });
}

export const useTicketComments = (o: string, ws: string, id: string) =>
  useQuery({ queryKey: ticketKeys.comments(o, ws, id), queryFn: () => api<Comment[]>(`${orgPath(o)}/workspaces/${ws}/tickets/items/${id}/comments`), enabled: Boolean(id) });

export function useAddTicketComment(o: string, ws: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api<Comment>(`${orgPath(o)}/workspaces/${ws}/tickets/items/${id}/comments`, 'POST', { body }),
    onSuccess: (c) => {
      qc.setQueryData<Comment[]>(ticketKeys.comments(o, ws, id), (l) => [...(l ?? []), c]);
      qc.invalidateQueries({ queryKey: ['ticket', o, ws, id] });
    },
  });
}

// ------------------------------------------------------------------ device notifications (Web Push)
import type { PushStatus } from '@unitly/contracts';

export const usePushStatus = (o: string, endpoint: string | null | undefined) =>
  useQuery({
    queryKey: ['push', o, endpoint ?? ''],
    queryFn: () => api<PushStatus>(`${orgPath(o)}/me/push${endpoint ? qs({ endpoint }) : ''}`),
    enabled: endpoint !== undefined,
  });

export function useSetPushSubscription(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { on: true; subscription: PushSubscriptionJSON } | { on: false; endpoint: string }) =>
      v.on
        ? api<void>(`${orgPath(o)}/me/push/subscriptions`, 'POST', v.subscription)
        : api<void>(`${orgPath(o)}/me/push/subscriptions`, 'DELETE', { endpoint: v.endpoint }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['push', o] }),
  });
}

export const useTestPush = (o: string) =>
  useMutation({ mutationFn: (endpoint: string) => api<{ delivered: number }>(`${orgPath(o)}/me/push/test`, 'POST', { endpoint }) });

// ------------------------------------------------------------------ developer: API keys and webhooks (admins)
import type { ApiKey, CreateApiKeyInput, CreateWebhookInput, CreatedApiKey, UpdateWebhookInput, Webhook, WebhookDelivery, WebhookWithSecret } from '@unitly/contracts';

const devKeys = {
  apiKeys: (o: string) => ['api-keys', o] as const,
  webhooks: (o: string) => ['webhooks', o] as const,
  deliveries: (o: string, id: string) => ['webhook-deliveries', o, id] as const,
};

export const useApiKeys = (o: string) => useQuery({ queryKey: devKeys.apiKeys(o), queryFn: () => api<ApiKey[]>(`${orgPath(o)}/api-keys`) });
export function useCreateApiKey(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CreateApiKeyInput) => api<CreatedApiKey>(`${orgPath(o)}/api-keys`, 'POST', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.apiKeys(o) }),
  });
}
export function useRevokeApiKey(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/api-keys/${id}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.apiKeys(o) }),
  });
}

export const useWebhooks = (o: string) => useQuery({ queryKey: devKeys.webhooks(o), queryFn: () => api<Webhook[]>(`${orgPath(o)}/webhooks`) });
export function useCreateWebhook(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: CreateWebhookInput) => api<WebhookWithSecret>(`${orgPath(o)}/webhooks`, 'POST', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.webhooks(o) }),
  });
}
export function useUpdateWebhook(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...v }: UpdateWebhookInput & { id: string }) => api<Webhook>(`${orgPath(o)}/webhooks/${id}`, 'PATCH', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.webhooks(o) }),
  });
}
export function useDeleteWebhook(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${orgPath(o)}/webhooks/${id}`, 'DELETE'),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.webhooks(o) }),
  });
}
export const useRotateWebhookSecret = (o: string) =>
  useMutation({ mutationFn: (id: string) => api<WebhookWithSecret>(`${orgPath(o)}/webhooks/${id}/rotate-secret`, 'POST') });
export function useTestWebhook(o: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ queued: boolean }>(`${orgPath(o)}/webhooks/${id}/test`, 'POST'),
    // The worker sends within a few seconds: refresh the log a little later.
    onSuccess: (_, id) => setTimeout(() => {
      void qc.invalidateQueries({ queryKey: devKeys.deliveries(o, id) });
      void qc.invalidateQueries({ queryKey: devKeys.webhooks(o) });
    }, 6000),
  });
}
export const useWebhookDeliveries = (o: string, id: string | null) =>
  useQuery({ queryKey: devKeys.deliveries(o, id ?? ''), queryFn: () => api<WebhookDelivery[]>(`${orgPath(o)}/webhooks/${id}/deliveries`), enabled: Boolean(id), refetchInterval: 10_000 });
